import { useEffect, useState } from "react";

import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import useStaticListSizeQuota from "app/pages/sales-intelligence/hooks/useStaticListSizeQuota";
import WebsitesModalTracked from "app/pages/sales-intelligence/sub-modules/opportunities/containers/ListModal/WebsitesModalTracked";
import { removeWhitespaces } from "app/pages/sales-intelligence/utils/strings";

import type { AddOpportunitiesToListDto, OpportunityListModal, OpportunityListType } from "../../../types";

type WebsitesModalWrapperProps = {
    modalPayload: Readonly<OpportunityListModal>;
    isListDataLoading: boolean;
    onToggle(isOpen: boolean, list?: OpportunityListType): void;
    onListCreateSubmit(name: string, domains?: readonly string[]): void;
    onListUpdateSubmit(dto: AddOpportunitiesToListDto): Promise<boolean>;
};

const MIN_LIST_NAME_LENGTH = 2;
const INPUT_LIMIT = 200;

const WebsitesModalWrapper = (props: WebsitesModalWrapperProps) => {
    const { modalPayload, isListDataLoading, onToggle, onListCreateSubmit, onListUpdateSubmit } = props;

    const { numberOfTrackedSites, limitOfWebsitesToTrack, isListSizeQuotaReached } = useStaticListSizeQuota();
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    const validateListName = (listName: string | undefined): boolean => {
        const cleanListName = removeWhitespaces(listName ?? "");

        return cleanListName.length >= MIN_LIST_NAME_LENGTH;
    };

    const changeNameHandler = (listName: string | undefined): void => {
        const isListNameValid = validateListName(listName);

        setIsSaveDisabled(!isListNameValid);
    };

    // This is how WebsitesModal works
    const getListName = (): string => {
        if (typeof modalPayload.list === "undefined") {
            return "";
        }

        return modalPayload.list.friendlyName;
    };

    // This is how WebsitesModal works
    const getListId = (): string => {
        if (typeof modalPayload.list === "undefined") {
            return "overview";
        }

        return modalPayload.list.opportunityListId;
    };

    async function handleSubmit(domains: readonly string[], name: string) {
        if (typeof modalPayload.list === "undefined") {
            return onListCreateSubmit(name, domains);
        }

        const isSuccessfullyUpdated = await onListUpdateSubmit({
            opportunities: domains,
        });

        if (!isSuccessfullyUpdated) {
            onToggle(false);
        }
    }

    function closeModal() {
        onToggle(false);
    }

    useEffect(() => {
        setIsSaveDisabled(Boolean(getListName()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useActionAfterFlagChange(isListDataLoading, closeModal);

    return (
        <WebsitesModalTracked
            isTrackingEnabled={typeof modalPayload.list === "undefined"}
            inputLimit={INPUT_LIMIT}
            isOpen={modalPayload.isOpen}
            onSave={handleSubmit}
            listName={getListName()}
            activeListId={getListId()}
            onCloseClick={closeModal}
            onCancelClick={closeModal}
            modalPlaceholder="si.components.list_modal.name_placeholder"
            placeholder="si.components.list_modal.websites_section.placeholder"
            isSaveDisabled={isSaveDisabled}
            onListNameChange={changeNameHandler}
            isOverQuotaLimit={isListSizeQuotaReached}
            quotaLimit={limitOfWebsitesToTrack}
            spentQuota={numberOfTrackedSites}
        />
    );
};

export default WebsitesModalWrapper;
